import { apiFetch } from "@/lib/apiClient";
import type { StatusTone } from "@/components/StatusPill";

export type EquipmentNeed = "NewEquipment" | "OwnHardware" | "VirtualTerminal" | "NoneNeeded";
export type EquipmentOrderStatus = "Draft" | "AwaitingApproval" | "Submitted" | "ReadyToShip" | "Shipped" | "Completed" | "Cancelled";
export type EquipmentPaymentOption = "Upfront" | "LeaseToOwn" | "FreeHardware";
export type EquipmentShipTo = "BusinessAddress" | "PickUpAtHq" | "OtherAddress";
export type EquipmentItemKind = "Hardware" | "Setup" | "Module" | "License" | "Bundle" | "OwnDevice" | "Service";

export interface EquipmentCatalogItem {
  code: string;
  name: string;
  category: string;
  kind: EquipmentItemKind;
  price: number;
  monthly: boolean;
  licenseCode: string | null;
  includes: string[] | null;
  active: boolean;
  note: string | null;
  maxQty: number | null;
}

export interface EquipmentFees {
  leaseFactor: number;
  leaseTermMonths: number;
  ownHardwareSetupFeePerDevice: number;
  ownHardwareMonthlyFeePerDevice: number;
  virtualTerminalSetupFee: number;
  virtualTerminalMonthlyFee: number;
}

export interface EquipmentCatalog {
  items: EquipmentCatalogItem[];
  fees: EquipmentFees;
  ownDeviceModels: string[];
  paybackOptions: number[];
  feesDecided: Record<string, boolean>;
}

export interface EquipmentPricedLine {
  code: string | null;
  name: string;
  kind: EquipmentItemKind;
  monthly: boolean;
  unitPrice: number;
  qty: number;
  coverPercent: boolean;
  coverValue: number;
  listTotal: number;
  agentCovers: number;
  merchantPays: number;
  auto: boolean;
  inBundle: boolean;
}

export interface EquipmentTotals {
  merchantDueAtInstall: number;
  merchantMonthly: number;
  leaseMonthly: number;
  agentCoversOneTime: number;
  agentCoversMonthly: number;
}

export interface EquipmentOrder {
  id: string;
  number: string;
  applicationId: string;
  applicationDba: string | null;
  applicationStatus: string | null;
  businessAddress: string | null;
  agentName: string | null;
  need: EquipmentNeed;
  status: EquipmentOrderStatus;
  payment: EquipmentPaymentOption;
  shipTo: EquipmentShipTo;
  shipAddress: string | null;
  paybackMonths: number | null;
  bundleCode: string | null;
  lines: EquipmentPricedLine[];
  totals: EquipmentTotals;
  notes: string | null;
  reviewFlags: string[] | null;
  problem: string | null;
  canEdit: boolean;
  viewerIsBackOffice: boolean;
  createdAt: string;
  submittedAt: string | null;
  applicationClosed: boolean;
  nextStatuses: EquipmentOrderStatus[];
  viewerIsAdmin: boolean;
}

export interface EquipmentLineInput {
  code: string | null;
  deviceName: string | null;
  qty: number;
  coverPercent: boolean;
  coverValue: number;
}

export interface EquipmentOrderSave {
  need: EquipmentNeed;
  payment: EquipmentPaymentOption;
  shipTo: EquipmentShipTo;
  shipAddress: string | null;
  paybackMonths: number | null;
  bundleCode: string | null;
  lines: EquipmentLineInput[];
  notes: string | null;
}

export interface EquipmentOrderListItem {
  id: string;
  number: string;
  applicationId: string;
  applicationDba: string | null;
  agentName: string | null;
  need: EquipmentNeed;
  status: EquipmentOrderStatus;
  summary: string;
  merchantDueAtInstall: number;
  merchantMonthly: number;
  agentCoversOneTime: number;
  agentCoversMonthly: number;
  flagged: boolean;
  createdAt: string;
  submittedAt: string | null;
  applicationClosed: boolean;
}

export interface EquipmentNeedsItem {
  applicationId: string;
  applicationDba: string | null;
  applicationStatus: string;
  agentName: string | null;
  draftOrderId: string | null;
  submittedAt: string | null;
}

export interface EquipmentOverview {
  viewerIsBackOffice: boolean;
  needsEquipment: EquipmentNeedsItem[];
  orders: EquipmentOrderListItem[];
}

export interface ApplicationEquipmentStatus {
  applicationId: string;
  done: boolean;
  orderId: string | null;
  number: string | null;
  need: EquipmentNeed | null;
  status: EquipmentOrderStatus | null;
  summary: string | null;
  submittedAt: string | null;
  draftOrderId: string | null;
  tracked: boolean;
  openOrderOnClosedApplication: boolean;
}

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentOrderStatus, string> = {
  Draft: "Draft",
  AwaitingApproval: "Waiting for admin approval",
  Submitted: "Submitted",
  ReadyToShip: "Ready to ship",
  Shipped: "Shipped",
  Completed: "Completed",
  Cancelled: "Cancelled",
};

export const EQUIPMENT_STATUS_TONES: Record<EquipmentOrderStatus, StatusTone> = {
  Draft: "muted",
  AwaitingApproval: "warning",
  Submitted: "warning",
  ReadyToShip: "info",
  Shipped: "success",
  Completed: "success",
  Cancelled: "muted",
};

export const EQUIPMENT_NEED_LABELS: Record<EquipmentNeed, string> = {
  NewEquipment: "New equipment",
  OwnHardware: "Own hardware",
  VirtualTerminal: "Virtual terminal",
  NoneNeeded: "No equipment needed",
};

export const EQUIPMENT_FULFILLMENT_STATUSES: EquipmentOrderStatus[] = ["AwaitingApproval", "Submitted", "ReadyToShip"];

export const equipment = {
  catalog() {
    return apiFetch<EquipmentCatalog>("/equipment/catalog");
  },
  overview() {
    return apiFetch<EquipmentOverview>("/equipment/overview");
  },
  applicationStatus(ids: string[]) {
    return apiFetch<ApplicationEquipmentStatus[]>(`/equipment/applications?ids=${encodeURIComponent(ids.join(","))}`);
  },
  get(id: string) {
    return apiFetch<EquipmentOrder>(`/equipment/orders/${encodeURIComponent(id)}`);
  },
  start(applicationId: string) {
    return apiFetch<EquipmentOrder>("/equipment/orders", { method: "POST", body: JSON.stringify({ applicationId }) });
  },
  save(id: string, body: EquipmentOrderSave) {
    return apiFetch<EquipmentOrder>(`/equipment/orders/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(body) });
  },
  submit(id: string) {
    return apiFetch<EquipmentOrder>(`/equipment/orders/${encodeURIComponent(id)}/submit`, { method: "POST" });
  },
  setStatus(id: string, status: EquipmentOrderStatus) {
    return apiFetch<EquipmentOrder>(`/equipment/orders/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
  },
};
